import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { concat, forkJoin, Observable, of, zip } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { DataService } from 'src/app/shared/data.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AirQualityService {
  private endPoint = `${environment.api}/air-qualities`;

  private dyName = 'air_quality';

  private updateSampleChartsUrl = `${environment.api}/sample-charts`;

  private updateMeasurementPointsUrl = `${environment.api}/measurement-points`;

  constructor(private http: HttpClient, private dataService: DataService) {}

  fetchData(): Observable<any> {
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        if (dy[this.dyName]) {
          return this.http.get(`${this.endPoint}/${dy[this.dyName]}`).pipe(
            switchMap((airQualityData: any) => {
              console.log('Fetched air_quality data:', airQualityData);
              const sampleFields = ['air_quality', 'workspace_air_quality', 'radiation'];
              const sampleRequests: Observable<any>[] = [];

              // For each sample field, if it has an ID, fetch the full sample-chart
              sampleFields.forEach(fieldName => {
                const sampleId = airQualityData[fieldName];
                if (sampleId && typeof sampleId === 'number') {
                  console.log(`Fetching sample-chart ${sampleId} for field ${fieldName}`);
                  sampleRequests.push(
                    this.http.get(`${this.updateSampleChartsUrl}/${sampleId}`).pipe(
                      map(sampleData => ({ fieldName, sampleData }))
                    )
                  );
                }
              });

              // If no samples to fetch, return air_quality data as-is
              if (sampleRequests.length === 0) {
                console.log('No sample-charts to fetch');
                return of(airQualityData);
              }

              // Fetch all sample-charts in parallel
              return forkJoin(sampleRequests).pipe(
                map(samples => {
                  console.log('Fetched samples:', samples);
                  // Replace IDs with full sample objects
                  samples.forEach(({ fieldName, sampleData }) => {
                    airQualityData[fieldName] = sampleData;
                  });
                  console.log('Final air_quality data with populated samples:', airQualityData);
                  return airQualityData;
                })
              );
            })
          );
        } else {
          return this.http.post(`${this.endPoint}`, {
            data_year: dy.id,
          });
        }
      })
    );
  }

  updateData(formValues: any): Observable<any[]> {
    console.log('AirQualityService.updateData called with:', formValues);

    for (const key of Object.keys(formValues)) {
      if (formValues[key].data?.id === undefined) {
        console.log('Deleting formValue without id:', key, formValues[key]);
        delete formValues[key];
      }
    }

    // Build map of field names to sample IDs
    const fieldToSampleId: { [key: string]: number } = {};
    for (const key of Object.keys(formValues)) {
      if (formValues[key].data?.id) {
        fieldToSampleId[key] = formValues[key].data.id;
      }
    }
    console.log('Field to sample ID mapping:', fieldToSampleId);

    const splittedObjects = this.splitFormValues(formValues);

    // Get air_quality ID from current year using Observable pattern
    return this.dataService.selectedYear.pipe(
      switchMap((dy) => {
        const airQualityId = dy[this.dyName];
        const updateRequests: Observable<any>[] = [];

        // Update sample-charts WITHOUT air_quality reference
        splittedObjects.sampleCharts.forEach((sample) => {
          if (sample.id) {
            console.log(`Updating sample-chart ${sample.id}:`, sample);
            updateRequests.push(
              this.http.put(`${this.updateSampleChartsUrl}/${sample.id}`, sample)
            );
          }
        });

        splittedObjects.measurementPoints.forEach((mp) => {
          if (mp.id) {
            console.log(`Updating measurement-point ${mp.id}:`, JSON.stringify(mp, null, 2));
            updateRequests.push(
              this.http.put(`${this.updateMeasurementPointsUrl}/${mp.id}`, mp).pipe(
                tap({
                  error: (error) => {
                    console.error(`Failed to update measurement-point ${mp.id}:`, error);
                    console.error('Measurement point data that failed:', JSON.stringify(mp, null, 2));
                    if (error.error?.data) {
                      console.error('Validation error details:', error.error.data);
                    }
                  }
                })
              )
            );
          }
        });

        // Update samples first, then air_quality table
        return (updateRequests.length > 0 ? forkJoin(updateRequests) : of([])).pipe(
          switchMap((sampleResults) => {
            console.log('Sample updates done. Now updating air_quality table with:', fieldToSampleId);
            return this.http.put(`${this.endPoint}/${airQualityId}`, fieldToSampleId).pipe(
              tap((response) => console.log('Air quality record updated:', response)),
              map((airQualityResponse) => [...sampleResults, airQualityResponse])
            );
          })
        );
      })
    );
  }

  private splitFormValues(formValues: any): {
    sampleCharts: any[];
    measurementPoints: any[];
  } {
    const sampleCharts: any[] = [];
    const measurementPoints: any[] = [];
    for (const key of Object.keys(formValues)) {
      if (formValues[key].data && Object.keys(formValues[key].data).length > 0) {
        if (formValues[key].data.points) {
          const points = formValues[key].data.points as Array<any>;
          delete formValues[key].data.points;
          points.forEach((p) => measurementPoints.push(p));
        }
        sampleCharts.push(formValues[key].data);
      }
    }
    return { sampleCharts, measurementPoints };
  }
}
